import { Feature } from "./shared/feature/feature.service";

interface NavAttributes {
    [propName: string]: any;
}

interface NavWrapper {
    attributes: NavAttributes;
    element: string;
}

interface NavBadge {
    text: string;
    color: string;
    variant: string;
    class?: string;
}

interface NavLabel {
    class?: string;
    variant: string;
}

export interface NavData {
    name?: string;
    url?: string;
    icon?: string;
    badge?: NavBadge;
    title?: boolean;
    children?: NavData[];
    variant?: string;
    attributes?: NavAttributes;
    divider?: boolean;
    class?: string;
    label?: NavLabel;
    wrapper?: NavWrapper;
    authorities?: string[];
    feature?: Feature;
    unlessFeature?: Feature;
}

// CoreUI renders the sidebar nav from these items and copies `attributes` onto each
// link and title, so their `data-qaa`s live here rather than in the layout template.
const SIDEBAR_NAV_QAA = "app-default-layout-sidebar-nav";

function navLinkQaa(url: string): NavAttributes {
    return { "data-qaa": `${SIDEBAR_NAV_QAA}-link`, "data-qaa-id": url };
}

function navTitleQaa(name: string): NavAttributes {
    return { "data-qaa": `${SIDEBAR_NAV_QAA}-title`, "data-qaa-id": name };
}

export const navItems: NavData[] = [
    {
        name: "Dashboard",
        url: "/dashboard",
        icon: "feather-home",
        attributes: navLinkQaa("/dashboard"),
    },
    {
        name: "Clients",
        url: "/client",
        icon: "feather-users",
        attributes: navLinkQaa("/client"),
    },
    {
        name: "Projects",
        url: "/project",
        icon: "feather-clipboard",
        feature: Feature.PROJECTS,
        attributes: navLinkQaa("/project"),
    },
    // {
    //     name: 'Calendar',
    //     url: '/calendar',
    //     icon: 'feather-calendar'
    // },
    {
        name: "Tasks",
        url: "/task",
        icon: "feather-list",
        feature: Feature.PROJECTS,
        attributes: navLinkQaa("/task"),
    },
    {
        name: "Client Actions",
        url: "/todo",
        icon: "feather-check-square",
        attributes: navLinkQaa("/todo"),
    },
    {
        name: "Proposals",
        url: "/proposal",
        icon: "feather-check-circle",
        feature: Feature.PROPOSALS,
        attributes: navLinkQaa("/proposal"),
    },
    {
        name: "Documents",
        url: "/document",
        icon: "feather-folder",
        attributes: navLinkQaa("/document"),
    },
    {
        name: "Contacts",
        url: "/contact",
        icon: "feather-phone",
        attributes: navLinkQaa("/contact"),
    },
    {
        name: "Time Tracking",
        url: "/time-tracking",
        icon: "feather-clock",
        feature: Feature.TIME_TRACKING,
        attributes: navLinkQaa("/time-tracking"),
    },
    {
        name: "Billing",
        url: "/billing-dashboard",
        icon: "feather-dollar-sign",
        authorities: ["ROLE_BILLING"],
        feature: Feature.BILLING,
        attributes: navLinkQaa("/billing-dashboard"),
    },
    {
        name: "Invoicing",
        url: "/invoice",
        icon: "feather-file-text",
        authorities: ["ROLE_BILLING"],
        feature: Feature.BILLING,
        attributes: navLinkQaa("/invoice"),
    },
    // {
    //     name: "ACH Migration",
    //     url: "/ach-migration",
    //     icon: "feather-refresh-cw",
    //     authorities: ["ROLE_BILLING"],
    //     feature: Feature.BILLING,
    // },
    {
        title: true,
        name: "Sales",
        feature: Feature.LEADS,
        attributes: navTitleQaa("Sales"),
    },
    {
        name: "Leads",
        url: "/lead",
        icon: "feather-user-plus",
        feature: Feature.LEADS,
        attributes: navLinkQaa("/lead"),
    },
    {
        title: true,
        name: "Reports",
        authorities: [
            "ROLE_REPORTING",
            "ROLE_BILLING",
            "ROLE_RECURRING_PROJECTS",
        ],
        attributes: navTitleQaa("Reports"),
    },
    {
        name: "View Reports",
        url: "/reporting",
        icon: "feather-briefcase",
        authorities: [
            "ROLE_REPORTING",
            "ROLE_BILLING",
            "ROLE_RECURRING_PROJECTS",
        ],
        attributes: navLinkQaa("/reporting"),
    },
    {
        title: true,
        name: "Client Portal",
        authorities: ["ROLE_MANAGE_CLIENT_PORTAL_ACCESS"],
        attributes: navTitleQaa("Client Portal"),
    },
    {
        name: "Access Logs",
        url: "/access-log",
        icon: "feather-inbox",
        authorities: ["ROLE_MANAGE_CLIENT_PORTAL_ACCESS"],
        feature: Feature.CLIENT_ACCESS,
        attributes: navLinkQaa("/access-log"),
    },
    {
        name: "Access Requests",
        url: "/registration",
        icon: "feather-user-check",
        authorities: ["ROLE_MANAGE_CLIENT_PORTAL_ACCESS"],
        badge: {
            text: null,
            color: "danger",
            variant: "bg-primary",
            class: "nav-badge-pill",
        },
        feature: Feature.CLIENT_ACCESS_REQUESTS,
        attributes: navLinkQaa("/registration"),
    },
    {
        title: true,
        name: "Admin",
        authorities: ["ROLE_ADMIN"],
        attributes: navTitleQaa("Admin"),
    },
    {
        name: "Users",
        url: "/user-management",
        icon: "feather-user",
        authorities: ["ROLE_ADMIN"],
        attributes: navLinkQaa("/user-management"),
    },
    {
        name: "Audit Logs",
        url: "/audit-log",
        icon: "feather-archive",
        authorities: ["ROLE_ADMIN"],
        feature: Feature.INTERNAL_ADMIN,
        attributes: navLinkQaa("/audit-log"),
    },
    {
        name: "Settings",
        url: "/system/settings",
        icon: "feather-settings",
        authorities: ["ROLE_ADMIN"],
        attributes: navLinkQaa("/system/settings"),
    },
    {
        name: "Customizations",
        url: "/system/customizations",
        icon: "feather-sliders",
        authorities: ["ROLE_ADMIN"],
        attributes: navLinkQaa("/system/customizations"),
    },
    {
        name: "Text Customizations",
        url: "/system/text-customizations",
        icon: "feather-edit",
        authorities: ["ROLE_ADMIN"],
        feature: Feature.INTERNAL_ADMIN,
        attributes: navLinkQaa("/system/text-customizations"),
    },
    {
        name: "Integrations",
        url: "/system/integrations",
        icon: "feather-shuffle",
        authorities: ["ROLE_ADMIN"],
        attributes: navLinkQaa("/system/integrations"),
    },
    {
        name: "Payments",
        url: "/system/payment-config",
        icon: "feather-credit-card",
        authorities: ["ROLE_ADMIN"],
        feature: Feature.BILLING,
        attributes: navLinkQaa("/system/payment-config"),
    },
    {
        name: "Document Export",
        url: "/system/document-export",
        icon: "feather-download",
        feature: Feature.DOCUMENT_EXPORTS,
        authorities: ["ROLE_ADMIN", "ROLE_DOCUMENT_EXPORT"],
        attributes: navLinkQaa("/system/document-export"),
    },
    {
        title: true,
        name: "Super User",
        authorities: ["ROLE_SUPERUSER"],
        attributes: navTitleQaa("Super User"),
    },
    {
        name: "Manage Firms",
        url: "/firm",
        icon: "feather-lock",
        authorities: ["ROLE_SUPERUSER"],
        attributes: navLinkQaa("/firm"),
    },
    {
        name: "Manage Variables",
        url: "/firm/variables",
        icon: "feather-lock",
        authorities: ["ROLE_SUPERUSER"],
        attributes: navLinkQaa("/firm/variables"),
    },
];
